import React, { useCallback } from 'react';

import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { useGqlContext } from '@search/gql-client/src';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';

import { UserEmailsQuery$variables as UserEmailsQueryVariables, UserEmailsQuery$data as UserEmailsQueryResponse } from '../../gql/__generated__/UserEmailsQuery.graphql';
import { userEmailsQuery } from '../../gql/UserEmailsQuery';

function useUserEmailsFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: UserEmailsQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            UserEmailsQueryVariables,
            UserEmailsQueryResponse
        >(userEmailsQuery, vars, opts).then(),
        [ gql ]
    );
}

export const useEmailsState = (emailText: string, onChangeEmail: (value: string) => void) => {
    const userEmailsLoader = useGql2Loader(useUserEmailsFetcher(), { cachePrefix: 'userEmails' });
    const { auth } = useM2AuthContext();
    const { data } = auth.isAuthenticated ? userEmailsLoader({}) : { data: undefined };
    const [ emailsData, setEmails ] = React.useState<{ label: string; value: string }[]>([]);
    const [ selectedEmail, setSelectedEmail ] = React.useState('');
    const [ isNewEmail, setIsNewEmail ] = React.useState(false);

    const updateIsNewEmail = useCallback((newValue?: string, newEmails?: { value: string; label: string }[]) => {
        const isEmailAlreadyExist = (newEmails ?? emailsData)
            .some(emailOption => emailOption.label === (newValue ?? emailText));

        setIsNewEmail(! isEmailAlreadyExist);
        if (newValue === data?.userEmails.primaryEmail) setIsNewEmail(false);
    }, [ emailText, emailsData ]);

    React.useEffect(() => {
        const emailsList = (data?.userEmails.emailsList ?? [])
            .map(email => ({ value: email ?? '', label: email ?? '' }));
        const defaultEmail = emailText || data?.userEmails.primaryEmail || '';

        setEmails(emailsList);
        setSelectedEmail(defaultEmail);
        onChangeEmail(defaultEmail);
        updateIsNewEmail(undefined, emailsList);
    }, [ data ]);

    React.useEffect(() => {
        updateIsNewEmail(emailText);
    }, [ emailText, updateIsNewEmail ]);

    const emailsSuggestions = React.useMemo(() => {
        return emailsData.filter(email => email.label.includes(emailText));
    }, [ emailText, emailsData ]);

    // eslint-disable-next-line require-await
    const setEmail = useCallback(async (value: string) => {
        setSelectedEmail(value);

        onChangeEmail(value);
        updateIsNewEmail(value);

        return true;
    }, [ updateIsNewEmail ]);

    const setEmailOption = useCallback((value: string | number = '') => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        setEmail(String(value)).then();
    }, [ setEmail ]);

    return {
        emailsSuggestions,
        selectedEmail,
        setEmail,
        setEmailOption,
        isNewEmail
    };
};
